import {
  Box,
  Button,
  Card,
  ClosableChips,
  Flex,
  Grid,
  Loader,
  NoData,
  Pagination,
  SearchBox,
  Text,
  Toastify,
} from "atoms";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Table from "molecules/Table";
import MemoEyeShowIcon from "assets/icons/EyeShow";
import MemoDeleteIcon from "assets/icons/DeleteIcon";
import MemoFilter from "assets/icons/Filter";
import { Filter } from "molecules";
import { useSort } from "hooks/useSort";
import { usePageNumber, usePageSize } from "hooks/usePageNumber";
import { usePropsoch } from "contexts/Propsoch";
import { format } from "date-fns";
import { PAGE_SIZE } from "utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import MemoArchive from "assets/icons/Archive";
import { ConfirmationModal } from "molecules/Modals";
import { useLocationQueryAdd } from "hooks/useLocationQuery";
import { optionFetch } from "utils/utilities";

const columns = [
  {
    title: "Property ID",
    field: "propertyId",
    sortKey: "propertyId",
    render: (row) => <>{row.propertyId}</>,
  },
  // {
  //   title: "Market",
  //   field: "market",
  //   sortKey: "market",
  //   render: (row) => <>{row.market}</>,
  // },
  // {
  //   title: "Micromarket",
  //   field: "micromarket",
  //   sortKey: "micromarket",
  //   render: (row) => <>{row.micromarket}</>,
  // },
  {
    title: "Property Name",
    field: "propertyName",
    sortKey: "propertyName",
    render: (row) => <>{row.propertyName}</>,
  },
  {
    title: "Area Type",
    field: "propertyAreaType",
    sortKey: "propertyAreaType",
    render: (row) => <>{row.propertyAreaType}</>,
  },
  {
    title: "RERA Number",
    field: "reraNumber",
    sortKey: "reraNumber",
    render: (row) => <>{row.reraNumber}</>,
  },
  {
    title: "Date",
    field: "date",
    sortKey: "date",
    render: (row) => <Text color="neutral.500">{row.date}</Text>,
  },

  {
    title: "Actions",
    rightAligned: true,
    render: (row) => (
      <Grid
        gridAutoFlow="column"
        gridGap="1.8rem"
        justifyContent="start"
        alignItems="center"
        color="neutral.600"
      >
        {row.publish ? (
          <Button height="4.3rem" variant="primary" px="2rem" width="13rem">
            Publish
          </Button>
        ) : (
          <Button
            height="4.3rem"
            variant="primaryOutline"
            px="2rem"
            width="13rem"
          >
            Unpublish
          </Button>
        )}
        <Box color="neutral.900" cursor="pointer">
          <MemoEyeShowIcon height="1.5rem" width="1.8rem" />
        </Box>
        <Box color="accents.100" cursor="pointer">
          <MemoDeleteIcon />
        </Box>
      </Grid>
    ),
  },
];

const Archive = () => {
  const {
    state: { userRole },
    actions: {
      getArchivePropertyListing,
      // changePublishStatus,
      changeArchiveStatus,
    },
  } = usePropsoch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setParam = useLocationQueryAdd();

  const [search, setSearch] = useState("");
  const [showFilter, setsShowFilter] = useState(false);
  const [locations, setLocations] = useState([]);
  const [micromarkets, setMicromarkets] = useState([]);
  useEffect(() => {
    // typology
    optionFetch("/micromarkets/get", "name", "id").then((data) => {
      setMicromarkets(data);
    });
    // typology
    optionFetch("/locations/get", "name", "id").then((data) => {
      setLocations(data);
    });
  }, []);

  // fields
  const [propertyListing, setPropertyListing] = useState([]);
  const [propertyListingLoading, setPropertyListingLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const sort = useSort();
  const page = Number(usePageNumber());
  const pageSize = Number(usePageSize());
  const locationFilter = searchParams.get("location");
  const isPublishedFilter = searchParams.get("isPublished");
  const micromarketFilter = searchParams.get("micromarket");
  const searchFilter = searchParams.get("search");

  // filterParamHandler starts
  const filterParamHandler = useCallback(() => {
    const params = {
      location: locationFilter,
      isPublished: isPublishedFilter,
      micromarket: micromarketFilter,
      search: searchFilter,
    };

    return params;
  }, [locationFilter, isPublishedFilter, micromarketFilter, searchFilter]);

  // fetch property Listing
  const fetchPropertyListing = useCallback(() => {
    setPropertyListingLoading(true);
    getArchivePropertyListing({
      sortBy: sort?.split(":")?.[0] || "createdAt",
      order: sort?.split(":")?.[1] || "desc",
      limit: pageSize,
      page: page,
      ...filterParamHandler(),
    })
      .then((data) => {
        setPropertyListing(data?.rows);
        setTotal(data?.count);
      })
      .finally(() => {
        setPropertyListingLoading(false);
      });
  }, [getArchivePropertyListing, page, pageSize, sort, filterParamHandler]);

  useEffect(() => {
    fetchPropertyListing();
  }, [fetchPropertyListing]);

  // const handlePublishChange = useCallback(
  //   (id, status) => {
  //     changePublishStatus(id, status).finally(() => {
  //       fetchPropertyListing();
  //     });
  //   },
  //   [changePublishStatus, fetchPropertyListing]
  // );

  const handleArchiveChange = useCallback(
    async (id, status) => {
      await changeArchiveStatus(id, status).finally(() => {
        fetchPropertyListing();
      });
    },
    [changeArchiveStatus, fetchPropertyListing]
  );
  const [archiveModal, setArchiveModal] = useState(false);

  const isAuthorisedToUnrchive = useMemo(() => {
    return userRole?.isSuperadmin || userRole?.isAdmin;
  }, [userRole?.isSuperadmin, userRole?.isAdmin]);

  const isAuthorisedToViewAndEdit = useMemo(() => {
    return userRole?.isSuperadmin || userRole?.isAdmin;
  }, [userRole?.isSuperadmin, userRole?.isAdmin]);

  return (
    <Box>
      <Flex justifyContent="space-between" aI="center" mt="1.8rem">
        <Text color="primary.900" fontSize="2.4rem" fontFamily="NeometricBold">
          Archived Properties
          <Text
            as="span"
            fontSize="1.6rem"
            fontFamily="NeometricRegular"
            pl="1rem"
          >
            {propertyListing?.length || 0} Properties
          </Text>
        </Text>
        {/* <Button
          variant="primary"
          width="fit-content"
          px="2rem"
          height="4.8rem"
          onClick={() => navigate("add")}
        >
          <Flex justifyContent="center" alignItems="center" gridGap="2rem">
            <MemoAdd />
            Add New Property
          </Flex>
        </Button> */}
      </Flex>
      {/* properties listing */}
      <Card my="2.4rem">
        <Flex mb="2.4rem" alignItems="center">
          <SearchBox
            value={search}
            onChange={(e) => {
              setSearch(e?.currentTarget?.value);
              setParam("search", e?.currentTarget?.value);
            }}
            onClose={() => {
              setSearch("");
              setParam("search", "");
            }}
            placeholder="Search"
            style={{ width: "30rem" }}
            removeValidationIcon={!search}
          />
          <Grid
            placeItems="center"
            height="4.8rem"
            width="6.8rem"
            borderRadius="1rem"
            border="1px solid"
            borderColor="primary.900"
            color="primary.900"
            ml="2.4rem"
            cursor="pointer"
            onClick={() => setsShowFilter(!showFilter)}
          >
            <MemoFilter height="1.8rem" width="1.8rem" />
          </Grid>
          {showFilter && (
            <Box position="absolute" zIndex={2} top="6rem">
              <Filter
                onClose={() => setsShowFilter(false)}
                locations={locations}
                micromarkets={micromarkets}
              />
            </Box>
          )}
          {(locationFilter || micromarketFilter || isPublishedFilter) && (
            <Text
              color="secondary.700"
              fontSize="1.6rem"
              fontFamily="NeometricBold"
              ml="2.4rem"
              cursor="pointer"
              onClick={() => {
                setParam("location", "");
                setParam("micromarket", "");
                setParam("isPublished", "");
              }}
            >
              Clear All
            </Text>
          )}
        </Flex>

        <Flex mb="2.4rem" gridGap="1rem">
          {locationFilter && (
            <ClosableChips
              name={
                locations?.find((i) => i?.value?.toString() === locationFilter)
                  ?.label || ""
              }
              onClick={() => {
                setParam("location", "");
              }}
            />
          )}
          {micromarketFilter && (
            <ClosableChips
              name={
                micromarkets?.find(
                  (i) => i?.value?.toString() === micromarketFilter
                )?.label || ""
              }
              onClick={() => {
                setParam("micromarket", "");
              }}
            />
          )}
          {
            {
              true: (
                <ClosableChips
                  name="Published"
                  onClick={() => {
                    setParam("isPublished", "");
                  }}
                />
              ),
              false: (
                <ClosableChips
                  name="Unpublished"
                  onClick={() => {
                    setParam("isPublished", "");
                  }}
                />
              ),
            }[isPublishedFilter]
          }
        </Flex>

        <Loader loading={propertyListingLoading}>
          <Table
            tableHeader={columns.map(({ title, rightAligned, sortKey }) => ({
              name: title,
              rightAligned,
              sortKey,
              containerProps: {
                fontSize: "1.4rem",
                fontWeight: "600",
              },
            }))}
            borderColor="neutral.500"
          >
            {propertyListing?.map((property, index) => (
              <tr key={index} style={{ verticalAlign: "top" }}>
                {/* property id */}
                <td>{property?.id}</td>
                {/* market */}
                {/* <td>{property?.micromarketId}</td> */}
                {/* micro market */}
                {/* <td>{property?.micromarketId}</td> */}
                {/* property name */}
                <td>{property?.name}</td>
                {/* area type */}
                <td>{property?.propertyAreaType || "-"}</td>
                {/* rera number */}
                <td>
                  <Flex maxWidth="24rem" css={{ overflowWrap: "anywhere" }}>
                    {property?.reraNumber || "-"}
                  </Flex>
                </td>
                {/* date */}
                <td>
                  <Box whiteSpace="nowrap">
                    {property?.createdAt &&
                      format(new Date(property?.createdAt), "dd MMM, yyyy")}
                  </Box>
                </td>
                {/* actions */}
                <td>
                  <Grid
                    gridAutoFlow="column"
                    gridGap="1.8rem"
                    justifyContent="start"
                    alignItems="center"
                    color="neutral.600"
                  >
                    <Button
                      height="4.3rem"
                      variant="primary"
                      px="2rem"
                      width="13rem"
                      onClick={() => {
                        setArchiveModal({
                          onClose: () => setArchiveModal(false),
                          icon: {
                            Icon: MemoArchive,
                            ICprops: { color: "accents.100" },
                          },
                          textOne: "Are you sure you want to",
                          textTwo: "unarchive",
                          textThree: `the property ${property?.name}`,
                          onConfirm: () => {
                            handleArchiveChange(property?.id, false)
                              .then(() => {
                                Toastify(
                                  "success",
                                  `${property?.name} unarchived successfully`
                                );
                              })
                              .catch(() => {
                                Toastify(
                                  "error",
                                  `Failed to unarchived ${property?.name}`
                                );
                              });
                            setArchiveModal(false);
                          },
                        });
                      }}
                      disabled={!isAuthorisedToUnrchive}
                    >
                      Unarchive
                    </Button>
                    <Box
                      color={
                        isAuthorisedToViewAndEdit
                          ? "neutral.900"
                          : "neutral.300"
                      }
                      cursor={
                        isAuthorisedToViewAndEdit ? "pointer" : "not-allowed"
                      }
                      onClick={
                        isAuthorisedToViewAndEdit
                          ? () =>
                              navigate(`/property-listing/add/${property?.id}`)
                          : undefined
                      }
                    >
                      <MemoEyeShowIcon height="1.5rem" width="1.8rem" />
                    </Box>
                    {/* <Box
                      color="accents.100"
                      cursor="pointer"
                      onClick={() => handleArchiveChange(property?.id, true)}
                    >
                      <MemoArchive />
                    </Box> */}
                  </Grid>
                </td>
              </tr>
            ))}
          </Table>
          {(!propertyListing || propertyListing?.length === 0) && <NoData />}
        </Loader>

        {total > PAGE_SIZE && <Pagination totalRecords={total} showPageSize />}
        {archiveModal && <ConfirmationModal {...archiveModal} />}
      </Card>
    </Box>
  );
};

export default Archive;
